// ConfirmDialog — project-level destructive-action confirmation modal.
//
// The WHP internal design system ships a single `.modal` primitive plus a
// `.btn` with `data-variant="danger"`, but does not ship a dedicated
// AlertDialog. The migration decision (see plans/) is to compose one at the
// project level rather than fork the DS — keeping DS purity while giving the
// app a single import for delete confirmations across HOA pages (and future
// callers). Focus lands on Cancel by default so a stray Enter keypress does
// not destroy data; Escape closes via the same path as Cancel.

import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  onConfirm: () => void;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  /** Label shown on the confirm button while `busy` is true. */
  busyLabel?: string;
  /** Optional inline error rendered between description and footer. */
  error?: string | null;
  /** Optional extra body content (e.g. preview of what's being deleted). */
  children?: React.ReactNode;
  /** Keep Confirm disabled until the body has what it needs (e.g. a required pick). */
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  onConfirm,
  tone = 'danger',
  busy = false,
  busyLabel,
  error,
  children,
  confirmDisabled = false,
}: ConfirmDialogProps): React.JSX.Element | null {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const containerRef = useFocusTrap(open);

  const handleEscape = useCallback(() => {
    if (!busy) onOpenChange(false);
  }, [busy, onOpenChange]);
  useEscapeKey(handleEscape, open);
  useInertBackground(open);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const confirmVariant = tone === 'danger' ? 'danger' : 'primary';
  const titleId = 'confirm-dialog-title';
  const descId = description ? 'confirm-dialog-desc' : undefined;

  const dialog = (
    <>
      <div
        className="modal-backdrop"
        onClick={() => { if (!busy) onOpenChange(false); }}
      />
      <div
        ref={containerRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="modal"
        data-size="sm"
      >
        <div className="modal-header">
          <h2 id={titleId} className="modal-title">{title}</h2>
        </div>
        <div className="modal-body">
          {description && (
            <p id={descId} className="modal-description">
              {description}
            </p>
          )}
          {children}
          {error && (
            <div className="alert" data-tone="danger" style={{ marginTop: 'var(--space-5)' }}>
              <svg
                className="alert-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              <div className="alert-body">{error}</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            ref={cancelRef}
            type="button"
            className="btn"
            data-variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            data-variant={confirmVariant}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? (busyLabel ?? confirmLabel) : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}
